import { TradingFormValues } from '@oneview/components/~advisor/Trading/Trading/TradingForm';
import { IndiaBank } from '@oneview/components/~client/BankLinks/constant';
import { apiConfig } from '@oneview/config';
import { yearMonthDayNoSeparator } from '@oneview/constants/datePattern';
import {
    AccountClassification,
    AccountType,
    ActivityType,
    ConsensusFormatOptions,
    CorporateActionsType,
    MiscOrderTransactionType,
    MoneyTransferMechanism,
    OptionExpirationStatus,
    OptionOrderTransaction,
    ShortConsensusFormat,
} from '@oneview/constants/lookups';
import { OrderType } from '@oneview/types/types';
import { AgreementCode } from '@orbis-systems/orbis-agreements-react';
import { UmaAuthAppType } from '@orbis-systems/orbis-api-clients/uma';
import {
    BasicMaterialIndustryCode,
    BondCouponFrequency,
    BondScreenerCriteria,
    CommunicationIndustryCode,
    ConsumerCyclicalIndustryCode,
    ConsumerDefensiveIndustryCode,
    EnergyIndustryCode,
    FinancialServicesIndustryCode,
    HealthcareIndustryCode,
    IndustrialsIndustryCode,
    OptionStrategy,
    OrderStatus,
    OrderTransaction,
    PositionType,
    Quote,
    QuoteType,
    RealEstateIndustryCode,
    StockScreenerCriteria,
    StockScreenerExchangeCode,
    StockScreenerIndustryCode,
    StockScreenerSectorCode,
    TechnologyIndustryCode,
    TransfersDirection,
    UtilitiesIndustryCode,
} from '@orbis-systems/orbis-api-middleware';
import { Currency, formatDate, SecurityType, titleCase, TradeDestination } from '@orbis-systems/orbis-react-toolkit';
import { isNull, startCase } from 'lodash';
import { formatMoneyWithCurrencyCode } from './formatMoneyWithCurrencyCode';
import { InsightFundScreenerRequestFundTypeEnum } from '@orbis-systems/orbis-api-clients/insight';

export const getAvatarUrl = (id: string | number, context: 'users' | 'accounts', withTimestamp = false) =>
    `${apiConfig.middlewareUrl}/v1/avatars/${context}/${id}${withTimestamp ? `?t=${Date.now()}` : ''}`;

export const getQuoteType = (type: QuoteType | string): string => {
    switch (type) {
        case QuoteType.Delayed:
            return '20-min delayed quotes';
        case QuoteType.Realtime:
        case QuoteType.NBBO:
            return 'Real-time quotes';
        case QuoteType.BATS:
            return 'BZX quotes';
        case QuoteType.QuoddEod:
            return 'QUODD EOD';
        case QuoteType.EquiductDelay:
            return 'Equiduct Delay';
        case QuoteType.EquiductRealtime:
            return 'Equiduct Realtime';
        default:
            return `${type} quotes`;
    }
};

export const getQuoteTypeShortened = (type: QuoteType | string): string => {
    switch (type) {
        case QuoteType.Delayed:
            return 'DL';
        case QuoteType.Realtime:
        case QuoteType.NBBO:
            return 'RT';
        case QuoteType.BATS:
            return 'BZX';
        case QuoteType.CBOE1:
            return 'CB1';
        case QuoteType.QuoddEod:
            return 'EOD';
        case QuoteType.EquiductRealtime:
            return 'EQ-RT';
        case QuoteType.EquiductDelay:
            return 'EQ-DL';
        default:
            return type;
    }
};

export const getConstantString = (constant: string): string => {
    switch (constant) {
        case AccountClassification.Cash:
            return 'Cash';
        case AccountClassification.Margin:
            return 'Margin';
        case AccountClassification.DayTrader:
            return 'Day Trader';
        case AccountClassification.IraRoth:
            return 'Roth IRA';
        case AccountClassification.IraSep:
            return 'Sep IRA';
        case AccountClassification.IraTraditional:
            return 'Traditional IRA';
        case AccountClassification.IraRolloverAccount:
            return 'Rollover IRA';

        case ActivityType.MoneyMovements:
            return 'CAA - Money movements';
        case ActivityType.StockActivity:
            return 'STA - Stock activity';
        case MiscOrderTransactionType.STA:
            return 'STA - Stock activity';
        case ActivityType.StockCashDividend:
            return 'DIV - Stock/cash dividend';
        case ActivityType.Transfer:
            return 'TRA - Transfer';
        case ActivityType.MandatoryCorporateAction:
            return 'MCA - Mandatory corporate action';
        case ActivityType.StockReceived:
            return 'REC - Stock received';
        case ActivityType.StockDelivered:
            return 'DEL - Stock delivered';
        case ActivityType.StockSplit:
            return 'SPL - Stock split';
        case ActivityType.ReverseStockSplit:
            return 'SPR - Reverse stock split';
        case ActivityType.BackofficeCashUpdate:
            return 'CAS - Backoffice cash update';
        case ActivityType.OptionExpired:
            return 'EXP - Option expired';
        case ActivityType.OptionExercised:
            return 'EXC - Option exercised';
        case MiscOrderTransactionType.Sel:
            return 'Sell';
        case OrderTransaction.ShortSell:
            return 'SS';
        case MiscOrderTransactionType.SS:
            return 'SS';
        case OrderTransaction.BuyToCover:
            return 'BC';
        case MiscOrderTransactionType.BC:
            return 'BC';
        case OptionOrderTransaction.BuyToOpen:
            return 'Buy to open';
        case MiscOrderTransactionType.BTO:
            return 'Buy to open';
        case OptionOrderTransaction.BuyToClose:
            return 'Buy to close';
        case MiscOrderTransactionType.BTC:
            return 'Buy to close';
        case OptionOrderTransaction.SellToOpen:
            return 'Sell to open';
        case MiscOrderTransactionType.STO:
            return 'Sell to open';
        case OptionOrderTransaction.SellToClose:
            return 'Sell to close';
        case MiscOrderTransactionType.STC:
            return 'Sell to close';
        case TransfersDirection.Incoming:
            return 'Deposit';
        case TransfersDirection.Outgoing:
            return 'Withdrawal';
        case CorporateActionsType.CusipChange:
            return 'CUSIP Change';

        // Multi-factor auth apps
        case UmaAuthAppType.EmailCodeAuthAppType:
            return 'Email';
        case UmaAuthAppType.SmsCodeAuthAppType:
            return 'Phone';
        case UmaAuthAppType.TOTPAuthAppType:
            return 'TOTP';
        case UmaAuthAppType.WebAuthnAuthAppType:
            return 'Web Auth';
        case UmaAuthAppType.YubicoOTPAuthAppType:
            return 'Yubico OTP';

        // Order type
        case OrderType.TWAP:
            return 'TWAP';
        case OrderType.VWAP:
            return 'VWAP';
        case OrderType.POV:
            return 'POV';

        default:
            if (constant === 'ETF') {
                return constant;
            }

            return titleCase(constant);
    }
};

export const getConstatStringAnalystRatingConsensus = (
    consensus: string,
): ShortConsensusFormat | ConsensusFormatOptions => {
    switch (consensus) {
        case ConsensusFormatOptions.StrongBuy:
            return ShortConsensusFormat.StrongBuy;
        case ConsensusFormatOptions.StrongSell:
            return ShortConsensusFormat.StrongSell;
        case ConsensusFormatOptions.Sell:
            return ConsensusFormatOptions.Sell;
        case ConsensusFormatOptions.Buy:
            return ConsensusFormatOptions.Buy;
        case ConsensusFormatOptions.Neutral:
            return ConsensusFormatOptions.Neutral;
        default:
            return ConsensusFormatOptions.Neutral;
    }
};

export const getConstantStringOptionExpirations = (constant: string): string => {
    switch (constant) {
        case OptionExpirationStatus.Call:
            return 'Call';
        case OptionExpirationStatus.Put:
            return 'Put';
        case OptionExpirationStatus.Long:
            return 'Long';
        case OptionExpirationStatus.Short:
            return 'Short';
        default:
            return '';
    }
};

export const getMoneyTransferMechanismName = (name: string): string => {
    switch (name) {
        case MoneyTransferMechanism.Ach:
            return 'ACH';
        case MoneyTransferMechanism.Wire:
            return 'WIRE';

        default:
            return name;
    }
};

export const getAgreementName = (code: AgreementCode) => {
    switch (code) {
        case AgreementCode.NyseAmex:
            return 'NYSE/AMEX';
        case AgreementCode.DowJones:
            return 'Dow Jones Index';
        case AgreementCode.Options:
            return 'Options';
        case AgreementCode.Nasdaq:
            return 'NASDAQ';
        case AgreementCode.SP500:
            return 'S&P 500';
        case AgreementCode.Canada:
            return 'Canada';
        case AgreementCode.CboeGlobal:
            return 'CBOE Global';
        default:
            return code;
    }
};

export const getAccountType = (accountType: string): string => {
    switch (accountType) {
        case String(AccountType.Cash):
            return 'Cash';
        case String(AccountType.Margin):
            return 'Margin';
        default:
            return accountType;
    }
};

export const getPositionType = (positionType: PositionType): string => {
    switch (positionType) {
        case PositionType.Cash:
            return 'Cash';
        case PositionType.Margin:
            return 'Margin';
        case PositionType.Short:
            return 'Short';
        default:
            return 'N/A';
    }
};

export const convertOptionSymbol = (quote: Quote) => {
    const splicedPrice = quote.strikePrice.toString().split('.');
    const formattedDate = formatDate(quote.expirationDate, { pattern: yearMonthDayNoSeparator });

    return `${quote.rootSymbol.padEnd(6)}${formattedDate}${quote.putOrCall}${splicedPrice[0]?.padStart(5, '0')}${
        splicedPrice[1]?.padEnd(3, '0') || '000'
    }`;
};

export const formatOptionSymbol = (quote: Quote) =>
    `${quote.underlyingSymbol} ${quote.putOrCall === 'C' ? 'Call' : 'Put'} ${formatDate(quote.expirationDate, {
        short: true,
    })} ${formatMoneyWithCurrencyCode(quote.strikePrice)}`;

export const getLogoUrl = (symbol: string) => `https://logos-api.orbisfn.io/logos/${symbol}/square`;

type Address = {
    city: string;
    country: string;
    state: string;
    streets: string[];
    zipcode: string;
};

export const formatProfileAddress = (address: Address) => {
    return Object.prototype.hasOwnProperty.call(address, 'streets')
        ? `${address.streets.join(' ')}, ${address.city} ${address.state}, ${address.zipcode} ${address.country}`
        : '';
};

export const roundedPercentage = (value: number) => {
    if (value < 5) {
        return 'Top 5%';
    } else if (value < 10) {
        return 'Top 10%';
    } else if (value < 25) {
        return 'Top 25%';
    } else if (value < 50) {
        return 'Top 50%';
    } else if (value < 60) {
        return 'Bottom 60%';
    } else {
        return 'Below top 60%';
    }
};

export const consensusColumnSort = (value: ConsensusFormatOptions) => {
    switch (value) {
        case ConsensusFormatOptions.StrongBuy:
            return 5;
        case ConsensusFormatOptions.Buy:
            return 4;
        case ConsensusFormatOptions.Neutral:
            return 3;
        case ConsensusFormatOptions.Hold:
            return 2;
        case ConsensusFormatOptions.Sell:
            return 1;
        case ConsensusFormatOptions.StrongSell:
            return 0;
        default:
            return 0;
    }
};

export const formatStrategyType = (strategy: OptionStrategy | undefined) => {
    switch (strategy) {
        case OptionStrategy.BuyWrite:
            return {
                title: 'Buy Write',
                desc: "In one transaction, purchasing 100 shares of a stock while at the same time opening a position to Sell 1 Call. This strategy consists of writing a call covered by an equivalent long stock position. It provides a small hedge on the stock and allows an investor to earn premium income, in return for temporarily forfeiting much of the stock's upside potential.",
            };
        case OptionStrategy.ShortStrangle:
            return {
                title: 'Short Strangle',
                desc: 'An investor doing a short strangle simultaneously sells an out-of-the-money put and an out-of-the-money call. This approach is a neutral strategy with limited profit potential. A short strangle profits when the price of the underlying stock trades in a narrow range between the breakeven points. The maximum profit is equivalent to the net premium received for writing the two options, less trading costs.',
            };
        case OptionStrategy.LongStrangle:
            return {
                title: 'Long Strangle',
                desc: "In a long strangle—the more common strategy—the investor simultaneously buys an out-of-the-money call and an out-of-the-money put option. The call option's strike price is higher than the underlying asset's current market price, while the put has a strike price that is lower than the asset's market price. This strategy has large profit potential since the call option has theoretically unlimited upside if the underlying asset rises in price, while the put option can profit if the underlying asset falls. The risk on the trade is limited to the premium paid for the two options.",
            };
        case OptionStrategy.LongCallSpread:
            return {
                title: 'Long Call Spread',
                desc: "This strategy consists of buying a call option. Buying a call is for investors who want a chance to participate in the underlying stock's expected appreciation during the term of the option. If things go as planned, the investor will be able to sell the call at a profit at some point before expiration.",
            };
        case OptionStrategy.ShortCallSpread:
            return {
                title: 'Short Call Spread',
                desc: 'This strategy can profit from a steady stock price, or from falling implied volatility. A short call ratio spread means buying one call (an at-the-money call) and selling two calls at the same expiration but with a higher strike. This strategy is the combination of a bull call spread and a naked call, where the strike of the naked call is equal to the upper strike of the bull call spread. The investor is ideally hoping for a slow rally up to the strike where they have sold two calls or a sharp fall in implied volatility during the life of the options.',
            };
        case OptionStrategy.LongPutSpread:
            return {
                title: 'Long Put Spread',
                desc: 'The initial cost to initiate this strategy is low, and may even earn a credit, but the downside potential is substantial. The long ratio put spread is a 1x2 spread combining one short put and two long puts with a lower strike. All options have the same expiration date. This strategy is the combination of a bull put spread and a long put, where the strike of the long put is equal to the lower strike of the bull put spread. The investor is looking for either a sharp move lower in underlying stock or a sharp move higher in implied volatility during the life of the options.',
            };
        case OptionStrategy.ShortPutSpread:
            return {
                title: 'Short Put Spread',
                desc: 'This strategy can profit from a slightly falling stock price, or from a rising stock price. The short ratio put spread involves buying one put (at-the-money) and selling two puts of the same expiration but with a lower strike. This strategy is the combination of a bear put spread and a naked put, where the strike of the naked put is equal to the lower strike of the bear put spread. The investor hopes for a slow move lower to the strike where they sold two puts, a limited trading range for the underlying product or a sharp fall in implied volatility during the life of the options.',
            };
        case OptionStrategy.LongCalendarCallSpread:
            return {
                title: 'Long Calendar Call Spread',
                desc: 'This strategy combines a longer-term bullish outlook with a near-term neutral/bearish outlook. Short one call option and long a second call option with a more distant expiration is an example of a long call calendar spread. The strategy most commonly involves calls with the same strike (horizontal spread) but can also be done with different strikes (diagonal spread). The investor is looking for either a steady to slightly declining stock price during the life of the near-term option and then a move higher during the life of the far-term option, or a sharp move upward in implied volatility.',
            };
        case OptionStrategy.LongCalendarPutSpread:
            return {
                title: 'Long Calendar Put Spread',
                desc: 'This strategy combines a longer-term bearish outlook with a near-term neutral/bullish outlook. To enter a long-put calendar spread, an investor sells one near-term put option and buys a second put option with a more distant expiration. The strategy most commonly involves puts with the same strike (horizontal spread) but can also be done with different strikes (diagonal spread). The investor is looking for either a steady to slightly rising stock price during the life of the near-term option and then a move lower during the life of the far-term option, or a sharp rise in implied volatility levels.',
            };
        case OptionStrategy.IronCondor:
            return {
                title: 'Iron Condor',
                desc: 'To construct a short condor, the investor sells one call while buying another call with a higher strike and sells one put while buying another put with a lower strike. Typically, the call strikes are above and the put strikes below the current level of underlying stock, and the distance between the call strikes equals the distance between the put strikes. All the options must be of the same expiration.',
            };
        case OptionStrategy.IronButterfly:
            return {
                title: 'Iron Butterfly',
                desc: 'This strategy profits if the underlying stock is inside the wings of the iron butterfly at expiration. A short iron butterfly consists of being long a call at an upper strike, short a call and short a put at a middle strike, and long a put at a lower strike. The upper and lower strikes (wings) must both be equidistant from the middle strike (body), and all the options must be the same expiration. An alternative way to think about this strategy is a short straddle surrounded by a long strangle. It could also be considered as a bear call spread and a bull put spread. The investor is looking for the underlying stock to trade in a narrow range during the life of the options.',
            };
        case OptionStrategy.SyntheticLongStock:
            return {
                title: 'Synthetic Long Stock',
                desc: 'This strategy is a long futures position on the underlying stock. The long call and the short put combined simulate a long stock position. The net result entails the same risk/reward profile, though only for the term of the option: unlimited potential for appreciation, and large (though limited) risk should the underlying stock fall in value.',
            };
        case OptionStrategy.SyntheticShortStock:
            return {
                title: 'Synthetic Short Stock',
                desc: 'This strategy is a short futures position on the underlying stock. The long put and the short call combined simulate a short stock position. The net result entails the same risk/reward profile, though only for the term of the options: limited but large potential for appreciation if the stock declines, and unlimited risk should the underlying stock rise in value.',
            };
        default:
            return {
                title: 'Spread',
                desc: 'Custom spread',
            };
    }
};

export const getBondCriteriaTitle = (criteria: BondScreenerCriteria) => {
    switch (criteria) {
        case BondScreenerCriteria.SpRating:
            return 'S&P Rating';
        case BondScreenerCriteria.Tickers:
            return 'Underlying Symbol';
        default:
            return titleCase(criteria);
    }
};

export const getCouponFrequency = (frequency: BondCouponFrequency | string) => {
    switch (frequency) {
        case BondCouponFrequency.Quarterly:
            return 'Quarterly';
        case BondCouponFrequency.SemiAnnual:
            return 'Semi Annual';
        case BondCouponFrequency.AtMaturity:
            return 'At Maturity';
        case BondCouponFrequency.Annual:
            return 'Annual';
        case BondCouponFrequency.Monthly:
            return 'Montly';
        case BondCouponFrequency.Zero:
            return 'Zero';
        default:
            return frequency;
    }
};

export const getFundTypeTitle = (type: string) => {
    switch (type) {
        case InsightFundScreenerRequestFundTypeEnum.MF:
            return 'Mutual Fund';

        default:
            return type;
    }
};

export const getStockScreenerSectorTitle = (sector: StockScreenerSectorCode | string) => {
    switch (sector) {
        case StockScreenerSectorCode.BasicMaterial:
            return 'Basic Material';
        case StockScreenerSectorCode.CommunicationServices:
            return 'Communication Services';
        case StockScreenerSectorCode.ConsumerCyclical:
            return 'Consumer Cyclical';
        case StockScreenerSectorCode.ConsumerDefensive:
            return 'Consumer Defensive';
        case StockScreenerSectorCode.Energy:
            return 'Energy';
        case StockScreenerSectorCode.FinancialServices:
            return 'Financial Services';
        case StockScreenerSectorCode.Healthcare:
            return 'Healthcare';
        case StockScreenerSectorCode.Industrials:
            return 'Industrials';
        case StockScreenerSectorCode.RealEstate:
            return 'RealEstate';
        case StockScreenerSectorCode.Technology:
            return 'Technology';
        case StockScreenerSectorCode.Utilities:
            return 'Utilities';
        default:
            return sector;
    }
};

export const getStockScreenerExchangeTitle = (exchange: StockScreenerExchangeCode | string) => {
    switch (exchange) {
        case StockScreenerExchangeCode.AmericanStockExchangeAmex:
            return 'American Stock Exchange (AMEX)';
        case StockScreenerExchangeCode.Bats:
            return 'BATS';
        case StockScreenerExchangeCode.InvestorsExchange:
            return 'Investors Exchange';
        case StockScreenerExchangeCode.Nasdaq:
            return 'NASDAQ';
        case StockScreenerExchangeCode.NasdaqCapitalMarket:
            return 'NASDAQ (Capital Market)';
        case StockScreenerExchangeCode.NasdaqGlobalMarket:
            return 'NASDAQ/NMS (Global Market)';
        case StockScreenerExchangeCode.NasdaqGlobalSelectMarket:
            return 'NASDAQ/NGS (Global Select Market)';
        case StockScreenerExchangeCode.NyStockExchange:
            return 'New York Stock Exchange, Inc.';
        case StockScreenerExchangeCode.NyseArca:
            return 'New York Stock Exchange (ARCA)';
        case StockScreenerExchangeCode.OtherOtc:
            return 'Other OTC';
        default:
            return exchange;
    }
};

export const getStockScreenerIndustryTitle = (industry: StockScreenerIndustryCode | string) => {
    switch (industry) {
        case BasicMaterialIndustryCode.Agriculture:
            return 'Agriculture';
        case BasicMaterialIndustryCode.BuildingMaterials:
            return 'Building Materials';
        case BasicMaterialIndustryCode.Chemicals:
            return 'Chemicals';
        case BasicMaterialIndustryCode.ForestProducts:
            return 'Forest Products';
        case BasicMaterialIndustryCode.MetalsMining:
            return 'Metals Mining';
        case BasicMaterialIndustryCode.Steel:
            return 'Steel';
        case ConsumerCyclicalIndustryCode.FurnishingsFixturesAppliance:
            return 'Furnishings Fixtures Appliance';
        case ConsumerCyclicalIndustryCode.HomebuildingConstruction:
            return 'Homebuilding Construction';
        case ConsumerCyclicalIndustryCode.ManufacturingApparelFurniture:
            return 'Manufacturing ApparelFurniture';
        case ConsumerCyclicalIndustryCode.PackagingContainers:
            return 'Packaging Containers';
        case ConsumerCyclicalIndustryCode.PersonalServices:
            return 'Personal Services';
        case ConsumerCyclicalIndustryCode.Restaurants:
            return 'Restaurants';
        case ConsumerCyclicalIndustryCode.RetailCyclical:
            return 'Retail Cyclical';
        case ConsumerCyclicalIndustryCode.TravelLeisure:
            return 'Travel Leisure';
        case ConsumerCyclicalIndustryCode.VehiclesParts:
            return 'Vehicles Parts';
        case FinancialServicesIndustryCode.AssetManagement:
            return 'AssetManagement';
        case FinancialServicesIndustryCode.Banks:
            return 'Banks';
        case FinancialServicesIndustryCode.CapitalMarkets:
            return 'Capital Markets';
        case FinancialServicesIndustryCode.CreditServices:
            return 'Credit Services';
        case FinancialServicesIndustryCode.DiversifiedFinancialServices:
            return 'Diversified Financial Services';
        case FinancialServicesIndustryCode.Insurance:
            return 'Insurance';
        case RealEstateIndustryCode.RealEstateServices:
            return 'RealEstate Services';
        case RealEstateIndustryCode.Reits:
            return 'Reits';
        case ConsumerDefensiveIndustryCode.BeveragesAlcoholic:
            return 'Beverages Alcoholic';
        case ConsumerDefensiveIndustryCode.BeveragesNonAlcoholic:
            return 'Beverages Non Alcoholic';
        case ConsumerDefensiveIndustryCode.ConsumerPackagedGoods:
            return 'Consumer Packaged Goods';
        case ConsumerDefensiveIndustryCode.Education:
            return 'Education';
        case ConsumerDefensiveIndustryCode.RetailDefensive:
            return 'Retail Defensive';
        case ConsumerDefensiveIndustryCode.TobaccoProducts:
            return 'Tobacco Products';
        case HealthcareIndustryCode.Biotechnology:
            return 'Biotechnology';
        case HealthcareIndustryCode.DrugManufacturers:
            return 'DrugManufacturers';
        case HealthcareIndustryCode.HealthcarePlans:
            return 'HealthcarePlans';
        case HealthcareIndustryCode.HealthcareProvidersServices:
            return 'Healthcare Providers Services';
        case HealthcareIndustryCode.MedicalDevicesInstruments:
            return 'MedicalDevicesInstruments';
        case HealthcareIndustryCode.MedicalDiagnosticsResearch:
            return 'Medical Diagnostics Research';
        case HealthcareIndustryCode.MedicalDistribution:
            return 'Medical Distribution';
        case UtilitiesIndustryCode.UtilitiesIndependentPowerProducers:
            return 'Utilities Independent Power Producers';
        case UtilitiesIndustryCode.UtilitiesRegulated:
            return 'Utilities Regulated';
        case CommunicationIndustryCode.InteractiveMedia:
            return 'Interactive Media';
        case CommunicationIndustryCode.MediaDiversified:
            return 'Media Diversified';
        case CommunicationIndustryCode.TelecommunicationServices:
            return 'Telecommunication Services';
        case EnergyIndustryCode.OilGas:
            return 'Oil & Gas';
        case EnergyIndustryCode.OtherEnergySources:
            return 'Other Energy Sources';
        case IndustrialsIndustryCode.AerospaceDefense:
            return 'Aerospace Defense';
        case IndustrialsIndustryCode.BusinessServices:
            return 'Business Services';
        case IndustrialsIndustryCode.Conglomerates:
            return 'Conglomerates';
        case IndustrialsIndustryCode.Construction:
            return 'Construction';
        case IndustrialsIndustryCode.FarmHeavyConstructionMachinery:
            return 'FarmHeavy Construction Machinery';
        case IndustrialsIndustryCode.IndustrialDistribution:
            return 'Industrial Distribution';
        case IndustrialsIndustryCode.IndustrialProducts:
            return 'Industrial Products';
        case IndustrialsIndustryCode.Transportation:
            return 'Transportation';
        case IndustrialsIndustryCode.WasteManagement:
            return 'Waste Management';
        case TechnologyIndustryCode.Hardware:
            return 'Hardware';
        case TechnologyIndustryCode.Semiconductors:
            return 'Semiconductors';
        case TechnologyIndustryCode.Software:
            return 'Software';
        default:
            return industry;
    }
};

export const getStockCriteriaTitle = (criteria: StockScreenerCriteria) => {
    switch (criteria) {
        case StockScreenerCriteria.PeRatio:
            return 'P/E Ratio';
        case StockScreenerCriteria.AverageVolume:
            return 'Average Volume (30d)';
        case StockScreenerCriteria.DividendExDate:
            return 'Dividend Ex-Date';
        case StockScreenerCriteria.DividendPercent:
            return 'Dividend %';
        case StockScreenerCriteria.PriceChangePercent:
            return 'Change %';
        case StockScreenerCriteria.Gap:
            return 'Gap %';
        default:
            return titleCase(criteria);
    }
};

export const generateTradeBlotterName = (values: TradingFormValues, quote?: Quote | null): string => {
    return `${startCase(TradeDestination[values.tradeDestination])} ${values.accountOrModel || ''} ${
        values.securityType === SecurityType.Option ? values.contracts : values.quantity || ''
    } ${values.symbol} ${
        values.securityType === SecurityType.Option ? 'Option' : `${quote ? `@ ${quote.lastPrice}` : ''}`
    }`;
};

export const getOrderStatus = (status: OrderStatus | string) => {
    switch (status) {
        case OrderStatus.Place:
            return 'Waiting to be sent';
        case OrderStatus.AcceptedAtTheExchange:
            return 'Waiting for execution';
        case OrderStatus.Cancelled:
            return 'Cancellation completed';
        case OrderStatus.Filled:
            return 'Order executed (Filled)';
        case OrderStatus.PartiallyFilled:
            return 'Partially executed';
        case OrderStatus.WaitingForCancellation:
            return 'Waiting cancellation';
        case OrderStatus.WaitingForCancelReplace:
            return 'Cancel replace request placed';
        case OrderStatus.Rejected:
            return 'Order rejected';
        case OrderStatus.PendingPost:
            return 'Pending post';
        case OrderStatus.PendingAccount:
            return 'Pending account';
        case OrderStatus.PendingCancel:
            return 'Pending cancel';
        default:
            return status;
    }
};

export const wrapInBraces = (value: string, addBraces: boolean = true) => {
    return addBraces ? `(${value})` : value;
};

export const getCurrencySymbol = (currency: string = Currency.UsDollar, localeCode: string = 'en-US') => {
    const validatedCurrency = isNull(currency) ? Currency.UsDollar : currency;

    return (
        new Intl.NumberFormat(localeCode, {
            style: 'currency',
            currency: validatedCurrency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })
            .formatToParts(1)
            .find((part) => part.type === 'currency')?.value || '$'
    );
};

export const replaceUnderscoresWithSpaces = (input: string): string => input.replace(/_/g, ' ');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const defaultFormatter = (item: Record<string, any>, key: string, defaultValue: string = '-'): string => {
    return item[key] ? item[key] : defaultValue;
};

export const getIndiaBankManualLink = (value: string) => {
    if (value.includes(IndiaBank.ICICI)) {
        return 'https://d3lsxve2s6mplm.cloudfront.net/3Verse-Banking-Guides/ICICIFundFlow.pdf';
    } else if (value.includes(IndiaBank.HDFC)) {
        return 'https://d3lsxve2s6mplm.cloudfront.net/3Verse-Banking-Guides/HDFCFundFlow.pdf';
    } else {
        return 'https://d3lsxve2s6mplm.cloudfront.net/3Verse-Banking-Guides/FundingInstructionsBeneficiaryDetails.pdf';
    }
};

// Accepted phone formats: 1-111-111-1111 | 111-111-1111 | 1231231234 (10 digits) | 11231231234 (11 digits with 1 at beginning)
export const UsPhoneValidationRegex = new RegExp(/^\d{1}-\d{3}-\d{3}-\d{4}$|^\d{3}-\d{3}-\d{4}$|^\d{10}$|^\d{11}/);

// (\+91)? // optionally match '+91'
// (91)?   // optionally match '91'
// -?      // optionally match '-'
// \s*?    // optionally match whitespace
// (\d{3}) // compulsory match 3 digits
// (\d{4}) // compulsory match 4 digits
export const InPhoneValidationRegex = new RegExp(/(\+91)?(-)?\s*?(91)?\s*?(\d{3})-?\s*?(\d{3})-?\s*?(\d{4})/);
